import React, { useState } from 'react';
import {
  ApiButton,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Image,
  Progress,
  Typography,
} from 'web-library';
import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { CustomMutateReturn } from '../../../hooks/query';
import { PostGeneratePaynowQRResponse } from '../../../api/financial/model';
import { GenericErrorResponse } from '../../../api';

interface QRPaymentModalProps {
  modelOpen: boolean;
  onClose: () => void;
  useQrCodeMutate: CustomMutateReturn<
    PostGeneratePaynowQRResponse,
    AxiosError<GenericErrorResponse>,
    string,
    unknown
  >;
  onClickRegenerateQrCode: () => void;
  countDownDisplay?: string;
  isQrExpired?: boolean;
}

const useStyles = makeStyles()((theme) =>
({
  dialogContainer: {
    display: 'flex',
  },
  QrImageOuterContainer: {
    width: '100%',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  QrImageInnerContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  QrImage: {
    height: '100%',
    width: 200,
  },
  regenerateContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  extraNote: {
    width: '100%',
    textAlign: 'left',
  },
}));

const QRPaymentModal = ({
  modelOpen,
  onClose,
  useQrCodeMutate,
  onClickRegenerateQrCode,
  countDownDisplay = '',
  isQrExpired = false,
}: QRPaymentModalProps) => {
  const { classes } = useStyles();
  const { isLoading, isError, data } = useQrCodeMutate;
  const [isProcessPayment, setIsProcessPayment] = useState<boolean | undefined>();
  const { t } = useTranslation();

  const qrExpiryMessage = (
    <Typography>{t('components->>QrPaymentModal.QrExpiryMessage')}</Typography>
  );
  const qrPaymentInstruction = (
    <Typography>
      {t('components->>QrPaymentModal.QrPaymentInstruction1')} <br />
      {t('components->>QrPaymentModal.QrPaymentInstruction2')} <br />
      {t('components->>QrPaymentModal.QrPaymentInstruction3')}
    </Typography>
  );
  const qrCodeInstruction = (
    <Typography>
      {t('components->>QrPaymentModal.QrCodeInstruction1')} <br /> <br />
      {t('components->>QrPaymentModal.QrCodeInstruction2')} <br /> <br />
      {t('components->>QrPaymentModal.QrCodeInstruction3')}
    </Typography>
  );

  return (
    <Dialog open={modelOpen} maxWidth="md">
      <DialogTitle>PayNow {isProcessPayment ? 'QR Code' : 'Payment Instructions'}</DialogTitle>

      <DialogContent className={classes.dialogContainer}>
        <DialogContentText>
          {isProcessPayment &&
            !isLoading &&
            !isError &&
            (isQrExpired ? qrExpiryMessage : qrCodeInstruction)}
          {!isProcessPayment && qrPaymentInstruction}
        </DialogContentText>
        <DialogContentText>
          {isProcessPayment && (
            <Box className={classes.QrImageOuterContainer}>
              <Box className={classes.QrImageInnerContainer}>
                {isLoading && <Progress color="secondary" />}
                {isProcessPayment && (
                  <>
                    {!isQrExpired && data?.qr && (
                      <Image
                        className={classes.QrImage}
                        src={`data:image/png;base64,${data?.qr}`}
                        alt="PayNow QR Code"
                      />
                    )}
                    {isQrExpired && (
                      <Box className={classes.regenerateContainer}>
                        <Button onClick={onClickRegenerateQrCode} variant="text" color="primary">
                          Click to Regenerate QR Code
                        </Button>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Box>
          )}
        </DialogContentText>
        {isProcessPayment && !isLoading && !isError && (
          <DialogContentText>
            {!isQrExpired
              ? `Please complete the transaction within ${countDownDisplay}.`
              : `Do not refresh your browser or dismiss the QR code while you are in the process of payment.
              You will be automatically redirected to the success page once we have received your payment.
              This can take a few minutes.`}
          </DialogContentText>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          type="button"
          variant="text"
          onClick={() => {
            onClose();
            setIsProcessPayment(false);
          }}
        >
          Cancel
        </Button>
        {isProcessPayment || (
          <ApiButton
            id="button-qrPaymentModal-makePayment"
            type="button"
            status="idle"
            onClick={() => {
              setIsProcessPayment(true);
              onClickRegenerateQrCode();
            }}
          >
            Make Payment
          </ApiButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default QRPaymentModal;
